import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useBooking } from '@features/booking/state';
import { resolveDeepLink } from './deepLink';

/**
 * Deep-link redirect page for the `/search/...` family of routes.
 *
 * Pre-fills booking state with whatever valid params the URL carries and
 * navigates to the matching point in the booking flow. Supports partial
 * forms — `/search/:date` lands on party-size (and locks the date),
 * `/search/:date/:partySize` on service, the three/four-segment forms on
 * time. Also backs the `/waiting-list-alternative/.../:wlResId` route, where
 * `wlResId` marks the booking as a WL reschedule. All interpretation lives in
 * `resolveDeepLink`.
 */
const DeepLinkRedirectPage = () => {
  const { date, partySize, serviceType, centerHour, wlResId } = useParams<{
    date: string;
    partySize: string;
    serviceType: string;
    centerHour: string;
    wlResId: string;
  }>();
  const navigate = useNavigate();
  const booking = useBooking();

  useEffect(() => {
    // Wait for BookingProvider to finish loading from sessionStorage so our
    // updates aren't clobbered by the hydrate.
    if (booking.isLoading) return;

    const { path, updates } = resolveDeepLink({ date, partySize, serviceType, centerHour, wlResId });

    if (updates) {
      booking.updateBooking(updates);
    }
    navigate(path, { replace: true });
  }, [booking.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-brand" />
    </div>
  );
};

export default DeepLinkRedirectPage;
